import Swal from 'sweetalert2';
import {
  getSyncMessageServiceWorkerErrorKind,
  type SyncMessageServiceWorkerErrorKind,
} from 'utils/syncMessageServiceWorker';
import {
  reportSyncMessageStartupError,
  type SyncMessageStartupErrorType,
} from 'utils/reportSyncMessageStartupError';
import {countFailedPageLoad} from 'utils/syncMessageFailedPageLoads';

const SERVICE_WORKER_DISABLED_MESSAGE =
  'We detected that you have Service Workers disabled. Please go to your browser settings and enable them, or use a browser that supports them for the best experience!';
const SERVICE_WORKER_TAB_RECOVERY_MESSAGE =
  'We had trouble attaching the code runner in this tab. Please refresh this tab. If it fails again, quit your browser completely and open it again — it may have installed an update that will not finish until you restart.';
const SERVICE_WORKER_GENERIC_ERROR_MESSAGE =
  'We could not initialize the code runner. Please refresh this tab. If it still fails, quit your browser completely, open it again, and come back to this page.';
const SERVICE_WORKER_RESTART_MESSAGE =
  "The code runner couldn't start. Please quit your browser completely and open it again — it may have installed an update that needs a restart — then come back to this page.";
/** Failed page loads in one tab after which a refresh has evidently not helped. */
const REPEATED_FAILURE_THRESHOLD = 2;
const SERVICE_WORKER_STORAGE_UNAVAILABLE_MESSAGE =
  "Your browser couldn't save data for this site, so the code runner can't start. In Chrome, open Site settings for this site, allow cookies and site data, then clear this site's data and reload. Also check that your computer has free disk space. If it still fails, try a different Chrome profile or another browser.";

interface ServiceWorkerFailureParams {
  error?: unknown;
  serviceWorkerSupported: boolean;
}

/** Input to {@link classifyServiceWorkerFailure}. */
interface ClassifyServiceWorkerFailureParams extends ServiceWorkerFailureParams {
  /** Failed page loads in this tab, including this one. */
  sessionFailureCount?: number;
}

/** A classified failure: what to record, and what to tell the user. */
interface ClassifiedServiceWorkerFailure {
  errorType: SyncMessageStartupErrorType;
  /** The classified cause; `null` when service workers are unsupported. */
  errorKind: SyncMessageServiceWorkerErrorKind | null;
  message: string;
}

/**
 * Maps a sync-message service-worker startup failure onto its diagnostic type
 * and the matching user-facing message. Exported for tests; callers should
 * prefer {@link handleServiceWorkerError} or {@link reportServiceWorkerFailure}.
 *
 * @param params.error The underlying error, if any; used to classify the
 *   failure. Omitted → treated as a generic registration failure.
 * @param params.serviceWorkerSupported Whether the browser supports service
 *   workers; `false` short-circuits to the "disabled" prompt.
 * @param params.sessionFailureCount Failed page loads in this tab, including
 *   this one. Omitted → treated as the first. At `REPEATED_FAILURE_THRESHOLD`
 *   and above, refresh advice gives way to a browser restart; the disabled
 *   and site-storage messages are kept, since their steps already go beyond a
 *   refresh.
 * @returns The diagnostic type, classified kind, and the message to show.
 * @throws Never.
 */
export function classifyServiceWorkerFailure({
  error,
  serviceWorkerSupported,
  sessionFailureCount = 1,
}: ClassifyServiceWorkerFailureParams): ClassifiedServiceWorkerFailure {
  if (!serviceWorkerSupported) {
    return {
      errorType: 'service-worker-disabled',
      errorKind: null,
      message: SERVICE_WORKER_DISABLED_MESSAGE,
    };
  }

  const errorKind = getSyncMessageServiceWorkerErrorKind(error);
  if (errorKind === 'unsupported') {
    return {
      errorType: 'service-worker-disabled',
      errorKind,
      message: SERVICE_WORKER_DISABLED_MESSAGE,
    };
  }
  if (errorKind === 'storage_unavailable') {
    return {
      errorType: 'service-worker-registration-failed',
      errorKind,
      message: SERVICE_WORKER_STORAGE_UNAVAILABLE_MESSAGE,
    };
  }

  const errorType: SyncMessageStartupErrorType =
    errorKind === 'controller_unavailable'
      ? 'service-worker-tab-recovery-needed'
      : 'service-worker-registration-failed';
  if (
    sessionFailureCount >= REPEATED_FAILURE_THRESHOLD ||
    errorKind === 'script_evaluation_failed'
  ) {
    return {errorType, errorKind, message: SERVICE_WORKER_RESTART_MESSAGE};
  }
  if (errorKind === 'controller_unavailable') {
    return {errorType, errorKind, message: SERVICE_WORKER_TAB_RECOVERY_MESSAGE};
  }
  return {errorType, errorKind, message: SERVICE_WORKER_GENERIC_ERROR_MESSAGE};
}

/**
 * Records a sync-message service-worker startup failure without interrupting
 * the user.
 *
 * For the boot-time warm-up in `serviceWorkerController`, which runs from
 * `bootstrapCipApp` before the first render — so before routing, and therefore
 * also on pages with no code runner at all (the landing page, `/cipx/intro`),
 * where a modal about service workers means nothing to the reader. Pages that
 * can actually run code prompt through {@link handleServiceWorkerError}
 * instead, when `CodeProvider` asks for the runtime.
 *
 * @param params.error The underlying error, if any; captured in the diagnostic.
 * @param params.serviceWorkerSupported Whether the browser supports service
 *   workers.
 * @returns Resolves once the diagnostic write attempt settles.
 * @throws Never — the diagnostic write is best-effort and swallowed on failure.
 */
export async function reportServiceWorkerFailure(
  params: ServiceWorkerFailureParams,
): Promise<void> {
  const sessionFailureCount = countFailedPageLoad();
  const {errorType, errorKind} = classifyServiceWorkerFailure({
    ...params,
    sessionFailureCount,
  });
  await reportSyncMessageStartupError(errorType, params.error, {
    errorKind,
    sessionFailureCount,
  });
}

/**
 * Shows the user an appropriate recovery prompt for a fatal sync-message
 * service-worker startup failure and records a diagnostic to Firestore. The
 * message is chosen by {@link classifyServiceWorkerFailure}.
 *
 * Only for a page that actually wants the code runner, since the prompt is a
 * modal — `CodeProvider.requestPythonClient` is the caller, because that is
 * the first point where we know the runtime was wanted. Non-fatal telemetry
 * (e.g. update timeouts) reports directly via
 * {@link reportSyncMessageStartupError} without an alert.
 *
 * @param params.error The underlying error, if any; used to classify the
 *   message and captured in the diagnostic. Omitted → treated as a generic
 *   registration failure.
 * @param params.serviceWorkerSupported Whether the browser supports service
 *   workers; `false` short-circuits to the "disabled" prompt.
 * @returns Resolves once the diagnostic write attempt settles.
 * @throws Never — the diagnostic write is best-effort and swallowed on failure.
 */
export async function handleServiceWorkerError(
  params: ServiceWorkerFailureParams,
): Promise<void> {
  const sessionFailureCount = countFailedPageLoad();
  const {errorType, errorKind, message} = classifyServiceWorkerFailure({
    ...params,
    sessionFailureCount,
  });
  Swal.fire({
    icon: 'warning',
    text: message,
  });
  await reportSyncMessageStartupError(errorType, params.error, {
    errorKind,
    sessionFailureCount,
  });
}
